import React, {
  RefObject,
  useMemo,
  useState,
  useCallback,
  SyntheticEvent,
  FocusEvent,
  DetailedHTMLProps,
  useRef,
} from 'react';
import clsx from 'clsx';
import delay from 'lodash/delay';
import values from 'lodash/values';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import compact from 'lodash/compact';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import AutoCompletedInput from 'core/components/AutoComplete';
import Spinner from 'pages/Dashboard/components/Spinner';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  ClaimAdjustmentReasonCodeDTO,
} from 'dtos';
import ToolTip from 'core/components/Tooltip';
import {
  useSearchAdjustmentReasonCode,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  ReasonCode,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBAdjustmentCodePopover';

type Part = 'root' | 'select' | 'input';

type Props = {
  className?: string;
  id: string;
  value?: ReasonCode | null;
  label?: string;
  placeholder: string;
  readonly?: boolean;
  onChange: (value: ReasonCode | null) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  inputRef?: RefObject<HTMLInputElement>;
  resetOnEmpty?: boolean;
  required?: boolean;
  classes?: Partial<Record<Part, string>>;
};

type RowProps = DetailedHTMLProps<any, any> & {
  option: ClaimAdjustmentReasonCodeDTO;
  optionsCount: number;
  onClick: () => void;
};

function ReasonRow({
  option,
  onClick,
  ...props
}: RowProps) {
  return (
    <div
      {...props}
      tabIndex={-1}
      role="button"
      onClick={onClick}
      onKeyDown={noop}
      className="cursor-pointer p-2 flex font-semibold flex-row items-center hover:text-blue-500 border-b border-b-gray-200"
    >
      {option?.code}
      <ToolTip
        title={option?.description}
        placement="right"
      >
        <span className="!text-xs ml-2 truncate text-gray-600">
          {' - '}
          {option?.description}
        </span>
      </ToolTip>
    </div>
  );
}

export default function AdjustmentReasonCodeLookup(props: Props) {
  const {
    className,
    id,
    onChange,
    placeholder,
    value,
    readonly,
    label,
    inputRef,
    required = false,
    onBlur,
    classes,
  } = props;
  const [inputValue, setInputValue] = useState<string>(value?.code ?? '');
  const blockRef = useRef<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  const wrapperClasses = clsx({
    '!gap-0': isEmptyString(label),
    'gap-1': !isEmptyString(label),
  });
  const containerClasses = clsx('w-full', className, classes?.root);

  const {
    data: _options,
    isFetching: isSearching,
  } = useSearchAdjustmentReasonCode(
    inputValue,
    open && inputValue?.length > 0,
  );

  const options = useMemo(() => (isSearching ? [] : _options ?? []), [isSearching, _options]);

  const onClose = () => {
    setOpen(false);
  };

  const handleChange = (value: ReasonCode | ReasonCode[]) => {
    setOpen(false);
    onChange(value as ReasonCode);
  };

  const handleInputChange = useCallback(debounce((event: SyntheticEvent, newInputValue: string) => {
    if (open) {
      setInputValue(newInputValue);
    }
  }, 700), [open]);

  const noOptionsText = useMemo(() => (
    !isSearching && options?.length === 0 && isEmptyString(inputValue)
      ? 'Start typing to see reason codes'
      : 'No reason codes found'
  ), [options, inputValue, isSearching]);

  const isEmpty = useMemo(() => (
    isNil(value) || values(value).every(isEmptyString)
  ), [value]);

  return (
    <div className={containerClasses}>
      <Wrapper
        id="adjustment-reason-select"
        className={wrapperClasses}
        label={label}
        required={required}
        labelClasses="font-semibold text-xs text-gray-500"
      >
        <AutoCompletedInput<ReasonCode>
          id={id}
          blurOnSelect
          includeInputInList
          disabled={readonly}
          options={options ?? []}
          onOpen={() => { setOpen(true); }}
          onClose={onClose}
          open={open}
          getLabel={(option) => compact([
            option.code,
            option.description,
          ]).join(' - ')}
          renderOption={(props, option) => (
            <ReasonRow
              {...props}
              key={option.code}
              option={option}
            />
          )}
          loading={isSearching}
          inputRef={inputRef}
          value={value}
          openOnFocus
          className={clsx('w-full', classes?.select)}
          onChange={(value) => {
            blockRef.current = true;
            handleChange(value);
            delay(() => { blockRef.current = false; }, 250);
          }}
          filterOptions={(x) => x}
          placeholder={placeholder}
          onInputChange={handleInputChange}
          clearOnBlur={false}
          noOptionsText={noOptionsText}
          loadingText="Searching..."
          isOptionEqualToValue={(a, b) => (
            a?.code === (b as ReasonCode)?.code
          )}
          dropdownClassName={clsx('!overflow-hidden !w-96', {
            '!p-0': options?.length && isEmpty && !isSearching,
          })}
          onBlur={(e: FocusEvent<HTMLInputElement>) => {
            onBlur?.(e);
            if (notEmptyString(e.target?.value) && !blockRef.current) {
              handleChange({ code: e.target.value });
            }
          }}
          popupIcon={isSearching ? <Spinner /> : <div />}
          InputProps={{
            classes: { root: clsx('!pr-2', classes?.input) },
          }}
        />
      </Wrapper>
    </div>
  );
}
